import { Component, OnInit, Inject } from '@angular/core';

import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';

import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, map, take, forkJoin, Subscription } from 'rxjs';
import { MatCalendarCellClassFunction } from '@angular/material/datepicker';
import * as moment from 'moment-timezone';

import { ReservationFacility } from '../../firestore-models/reservation-facility';
import { ReservationFacilitySpecial } from '../../firestore-models/reservation-facility-special';
import { Reservation } from '../../firestore-models/reservation';

import { ProjectLanguage } from '../../firestore-models/project-language';

import { ConfigurationService } from '../../configuration.service';
import { AppConst } from '../../app-const';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';

export interface ReservationFacilityReservationDialogData {
  reservationFacility?: ReservationFacility;
}

export interface marker {
	lat: number;
	lng: number;
	draggable: boolean;
}

@Component({
  selector: 'app-reservation-facility-reservation-dialog',
  templateUrl: './reservation-facility-reservation-dialog.component.html',
  styleUrls: ['./reservation-facility-reservation-dialog.component.css'],
  standalone: false,
  providers: [
    // このコンポーネントとその子要素専用のDateAdapterインスタンスを作成 //(0703)
    { provide: DateAdapter, useClass: NativeDateAdapter },
  ]
})
export class ReservationFacilityReservationDialogComponent implements OnInit {

  //予約設備情報
  reservationFacility?: ReservationFacility;
  //各予約設備情報に紐付くプロジェクトID
  projectId: string;

  // isAdded: boolean = false;
  projectLanguages!: Array<ProjectLanguage>;

  //予約設備情報フォーム
  reservationFacilityFormGroup!: FormGroup;
  reservationTargetControl!: FormControl;
  reservationDateControl!: FormControl;
  reservationFacilityIdControl!: FormControl;

  reserveStartDate?: Date;
  reserveEndDate?: Date;
  ngDays: any[] = [];
  reservationStatuses: any[] = [];
  subscription = new Subscription();
  readonly TARGET_PRODUCTION: string = '0';
  readonly TARGET_PREVIEW: string = '1';
  targetReservationFacility: ReservationFacility | null = null;

  projectBaseLang: string = 'ja';
  currentLang: string = 'ja';
  private langChangeSub!: Subscription; // TranslateServiceの購読を管理

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate: any, view) => {
    let cssClass = '';
    const cellDateDate = cellDate.toDate();
    if (this.reserveStartDate && this.reserveEndDate) {
      if (this.reserveStartDate <= cellDate && cellDate <= this.reserveEndDate) {
        for (const date of this.ngDays) {
          if (date.date.getTime() === cellDateDate.getTime()) {
            if (date.reason === 'full') {
              cssClass = 'reservation-calendar-custom-color-full';
            } else {
              cssClass = 'reservation-calendar-custom-color-closed';
            }
          }
        }
      }
    }
    return cssClass;
  };

  constructor(
    public dialogRef: MatDialogRef<ReservationFacilityReservationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReservationFacilityReservationDialogData,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private angularFirestore: AngularFirestore,
    private configurationService: ConfigurationService,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<any>
  ) {
    this.projectId = this.configurationService.getProjectId();
    moment.tz.setDefault('Asia/Tokyo');
   }

  ngOnInit(): void {
    this.projectLanguages = this.configurationService.getProject().languages!;
    this.reservationFacility = this.data.reservationFacility;

    this.createForm();
    this.reservationTargetControl = this.reservationFacilityFormGroup.get('reservationTarget') as FormControl;
    this.reservationDateControl = this.reservationFacilityFormGroup.get('reservationDate') as FormControl;

    // 予約可能期間の算出
    const today = new Date();
    this.reserveStartDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
    this.reserveStartDate = new Date(this.reserveStartDate.setDate(this.reserveStartDate.getDate() + this.reservationFacility!.reservation_end_date).valueOf());
    this.reserveEndDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
    this.reserveEndDate = new Date(this.reserveEndDate.setDate(this.reserveEndDate.getDate() + this.reservationFacility!.reservation_start_date).valueOf());

    // 休業日と満席の日を取得
    this.subscription = this.getReservationDates().subscribe((ngDays) => {
      this.ngDays = ngDays
    });
    this.projectBaseLang = this.configurationService.getProject().project_base_lang;
    this.currentLang = this.translate.currentLang; //(0703)
    // 1. コンポーネント初期化時に現在の言語でカレンダーを設定
    this.setCalendarLocale(this.currentLang);

    // 2. アプリケーションの言語が変更されたら、カレンダーの言語も動的に追従させる
    this.langChangeSub = this.translate.onLangChange.subscribe(event => {
      this.setCalendarLocale(event.lang);
    });
  }

  ngOnDestory(): void {
    this.subscription.unsubscribe();
  }
 
  /** //(0703)
  * Datepickerのロケールを設定するプライベートメソッド
  * @param lang 設定したい言語コード (例: 'en', 'ja')
  */
  private setCalendarLocale(lang: string): void {
    // 言語に応じたロケールを設定
    const locale = lang === 'en' ? 'en-US' : 'ja-JP';
    this.dateAdapter.setLocale(locale);
  }
  
  //フォームの値を各要素に代入
  private createForm() {
    this.reservationFacilityFormGroup = this.formBuilder.group(
      {
        reservationFacilityId: [this.reservationFacility!.reservation_facility_id, []],
        reservationTarget: [this.TARGET_PRODUCTION, []],
        reservationDate: [null, []],
      }
  );
  }

  //閉じる
  onNoClick(): void {
    this.dialogRef.close();
  }

  //対象環境変更
  changeTarget() {
    this.subscription.unsubscribe();
    // 休業日と満席の日を取得
    this.subscription = this.getReservationDates().subscribe((ngDays) => {
      this.ngDays = ngDays
    });
    this.changeDate();
  }

  //利用日変更
  changeDate() {
    if (!this.reservationDateControl.value) {
      return;
    }
    const reservationDate = this.reservationDateControl.value.toDate();
    const facility = this.reservationFacility!;
    const reservationTarget = this.reservationTargetControl.value;
    const reservationStatuses: any[] = [];
    
    let today = new Date();
    today = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
    // 予約の取得範囲を(現在日 - 予約開始日数) ～ (現在日 + 予約開始日数)に設定
    let toDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
    toDate = new Date(toDate.setDate(toDate.getDate() + facility.reservation_start_date).valueOf());
    let fromDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
    fromDate = new Date(fromDate.setDate(fromDate.getDate() - facility.reservation_start_date).valueOf());
    // 期間タイプ
    if (facility.reservation_unit === 1) {
      const sub = forkJoin([
        this.getSpecialsByReservationFacilityId(facility, reservationTarget),
        this.getReservation(facility, fromDate, toDate),
        this.getReservationFacility(facility, reservationTarget),
      ]).subscribe((datas: [ReservationFacilitySpecial[], Reservation[] | undefined, ReservationFacility | undefined]) => {
        sub.unsubscribe();
        let specials = datas[0];
        let reservations = datas[1];
        let resservationFacility = datas[2];
        // 予約設備情報あり
        if (!!resservationFacility) {
          let reserveStartDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          reserveStartDate = new Date(reserveStartDate.setDate(reserveStartDate.getDate() + facility.reservation_end_date).valueOf());
          let reserveEndDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          reserveEndDate = new Date(reserveEndDate.setDate(reserveEndDate.getDate() + facility.reservation_start_date).valueOf());
  
          let dailyCount: Array<any> = [];
          // 予約可能日数分の予約件数リストの作成  [0]予約日 [1]予約件数（初期値0） [2]予約上限件数
          for (let date = new Date(reserveStartDate.valueOf()); date <= reserveEndDate; date.setDate(date.getDate() + 1)) {
            const tmpSearchDate = new Date(date.valueOf());
            let reserveCount = 0;
            if (facility.reservation_count) {
              reserveCount = facility.reservation_count;
            }
            if (specials) {
              for (let reservationFacilitySpecial of specials) {
                // 特別営業日で
                if (reservationFacilitySpecial.reservation_special_date_type===2) {
                  if (reservationFacilitySpecial.reservation_special_start_date) {
                    // 単日
                    if (!reservationFacilitySpecial.reservation_special_end_date) {
                      if (tmpSearchDate.getTime() === new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime()) {
                        if (reservationFacilitySpecial.reservation_special_count) {
                          reserveCount = reservationFacilitySpecial.reservation_special_count;
                        }
                      }
                    // 期間
                    } else {
                      if (new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime() <= tmpSearchDate.getTime() &&
                          tmpSearchDate.getTime() <= new Date(reservationFacilitySpecial.reservation_special_end_date.seconds*1000).getTime()) {
                        if (reservationFacilitySpecial.reservation_special_count) {
                          reserveCount = reservationFacilitySpecial.reservation_special_count;
                        }
                      }
                    }
                  }
                }
              }
            }
            dailyCount.push([tmpSearchDate, 0, reserveCount]);
          }
          // 予約件数リストの[1]予約件数を更新
          if (reservations) {
            for (let reservation of reservations) {
              // キャンセルと対象環境が違う予約は予約件数に含めない
              if (reservation.reservation_status===AppConst.RESERVATION_STATUS_CANCEL
                || reservationTarget === this.TARGET_PRODUCTION && reservation.preview 
                || reservationTarget === this.TARGET_PREVIEW && !reservation.preview
              ) {
                continue;
              }
              // キャンセル以外かつ対象環境の予約は日別に件数を集計する
              if (reservation.reservation_start_date) {
                if (!reservation.reservation_end_date) {
                  for (let dayCount of dailyCount) {
                    if (dayCount[0].getTime() === new Date(reservation.reservation_start_date.seconds*1000).getTime()) {
                      dayCount[1] += 1;
                    }
                  };
                } else {
                  for (let date = new Date(reservation.reservation_start_date.seconds*1000); date <= new Date(reservation.reservation_end_date.seconds*1000); date.setDate(date.getDate() + 1)) {
                    for (let dayCount of dailyCount) {
                      if (dayCount[0].getTime() === date.getTime()) {
                        dayCount[1] += 1;
                      }
                    }
                  }
                }
              }
            }
          }
          for (let dayCount of dailyCount) {
            if (dayCount[0].getTime() === reservationDate.getTime()) {
              reservationStatuses.push({
                index: null, 
                start_time: null,
                end_time: null,
                count: dayCount[1],
                value: Number(dayCount[2]),
              });
              break;
            }
          }
        }
        this.reservationStatuses = reservationStatuses;
      });
    // 時間枠タイプ
    } else {
      const sub = forkJoin([
        this.getSpecialsByReservationFacilityId(facility, reservationTarget),
        this.getReservation(facility, reservationDate, reservationDate),
        this.getReservationFacility(facility, reservationTarget),
      ]).subscribe((datas: [ReservationFacilitySpecial[], Reservation[] | undefined, ReservationFacility | undefined]) => {
        sub.unsubscribe();
        let specials = datas[0];
        let reservations = datas[1];
        let resservationFacility = datas[2];
        // 予約設備情報あり
        if (resservationFacility) {
          // 営業日種別が特別営業日以外か利用日が日付の範囲外の特別営業日情報は除外
          specials = specials.filter(special => {
            if (special.reservation_special_date_type === AppConst.RESERVATION_SPECIAL_DATE_TYPE_SPECIAL) {
              // 特別営業日情報の終了日がなく、開始日が抽選対象の利用日と一致
              if (!special.reservation_special_end_date && special.reservation_special_start_date!.toDate().getTime() == reservationDate.getTime()) {
                return true;
              }
              // 特別営業日情報の終了日があり、抽選対象の利用日が開始日と終了日の間
              if (!!special.reservation_special_end_date && special.reservation_special_start_date!.toDate().getTime() <= reservationDate.getTime() && special.reservation_special_end_date.toDate().getTime() >= reservationDate.getTime()) {
                return true;
              }
            }
            return false;
          }
            // special.reservation_special_date_type === AppConst.RESERVATION_SPECIAL_DATE_TYPE_SPECIAL
            //   && special.reservation_special_start_date!.toDate().getTime() <= reservationDate && special.reservation_special_end_date! >= reservationDate
          );
          //  キャンセルと対象環境が違う予約は予約件数に含めない
          reservations = reservations!.filter(
            reservation => reservation.reservation_start_date.toDate().getTime() === reservationDate.getTime()
            && reservation.reservation_status !== AppConst.RESERVATION_STATUS_CANCEL
            && (reservationTarget === this.TARGET_PRODUCTION && !reservation.preview || reservationTarget === this.TARGET_PREVIEW && reservation.preview)
          );
          // 特別日の営業日
          if (specials.length > 0) {
            const special = specials[0];
            for (const time of special.reservation_select_times) {
              reservationStatuses.push({
                index: time.index, 
                start_time: time.items[0] + ':' + time.items[1],
                end_time: time.items[2] + ':' + time.items[3],
                count: 0,
                value: Number(time.value),
              });
            }
          // 通常の営業日
          } else {
            for (const time of facility.reservation_select_times) {
              reservationStatuses.push({
                index: time.index, 
                start_time: time.items[0] + ':' + time.items[1],
                end_time: time.items[2] + ':' + time.items[3],
                count: 0,
                value: Number(time.value),
              });
            }
          }

          // 予約数をカウント
          for (const reservation of reservations) {
            for (const time of reservation.reservation_times) {
              for (const status of reservationStatuses) {
                if (time.index === status.index) {
                  status.count++;
                }
              }
            }
          }
        }
        this.reservationStatuses = reservationStatuses;
      });
    }
  }

  // 休業日や満席の利用日を取得
  private getReservationDates(): Observable<any[]> {
    return new Observable((observer) => {
      let retArray: any[] = [];
      const reservationTarget = this.reservationTargetControl.value;
      const facility = this.reservationFacility!;
      this.targetReservationFacility = null;
      this.reservationStatuses = [];
      // 予約施設のNG日（定休日＋休業日＋予約満席日）のリストを作成する
      const sub = forkJoin([
        this.getSpecialsByReservationFacilityId(facility, reservationTarget),
        this.getReservationFacility(facility, reservationTarget),
      ]).subscribe((datas: [ReservationFacilitySpecial[], ReservationFacility | undefined]) => {
        sub.unsubscribe();
        const fireSpecials = datas[0];
        const fireReservationFacility = datas[1];
        if (fireReservationFacility) {
          this.targetReservationFacility = fireReservationFacility;
          // 定休日
          const today = new Date();
          let reserveStartDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          reserveStartDate = new Date(reserveStartDate.setDate(reserveStartDate.getDate() + fireReservationFacility.reservation_end_date).valueOf());
          let reserveEndDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          reserveEndDate = new Date(reserveEndDate.setDate(reserveEndDate.getDate() + fireReservationFacility.reservation_start_date).valueOf());
          if (reserveStartDate && reserveEndDate) {
            for (let date=new Date(reserveStartDate.valueOf()); date <= reserveEndDate; date.setDate(date.getDate()+1)) {
              const day = (new Date(date)).getDay();
              if (fireReservationFacility.holidays && fireReservationFacility.holidays.length == 7) {
                if (fireReservationFacility.holidays[day] == true) {
                  retArray.push({date: new Date(date), reason: 'regular_holiday'});
                }
              }
            }
          }
          // 特別日（休業）
          if (fireSpecials) {
            for (const specialDate of fireSpecials) {
              if (specialDate.reservation_special_date_type == AppConst.RESERVATION_SPECIAL_DATE_TYPE_HOLIDAY) {
                if (specialDate.reservation_special_start_date) {
                  let startDate = specialDate.reservation_special_start_date.toDate();
                  if (specialDate.reservation_special_end_date) {
                    let endDate = specialDate.reservation_special_end_date.toDate();
                    for (let date=startDate; date <= endDate; date.setDate(date.getDate()+1)) {
                      if (retArray.filter(ret => ret.date.getTime() === new Date(date).getTime()).length === 0) {
                        retArray.push({date: new Date(date), reason: 'special_holiday'});
                      }
                    }
                  } else {
                    retArray.push({date: startDate, reason: 'special_holiday'});
                  }
                }
              }
            }
          }
          // 予約満席
          let fromDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          fromDate = new Date(fromDate.setDate(fromDate.getDate() - fireReservationFacility.reservation_start_date).valueOf());
          let toDate = new Date(today.getFullYear().valueOf(),today.getMonth().valueOf(),today.getDate().valueOf(),0,0,0);
          toDate = new Date(toDate.setDate(toDate.getDate() + fireReservationFacility.reservation_start_date).valueOf());
          this.getReservation(facility, fromDate, toDate).subscribe((reservations) => {
            if (reservations) {
              // 期間タイプ
              if (fireReservationFacility.reservation_unit===1) {
                let dailyCount: Array<any> = [];
                // 予約可能日数分の予約件数リストの作成  [0]予約日 [1]予約件数（初期値0） [2]予約上限件数
                for (let date = new Date(reserveStartDate.valueOf()); date <= reserveEndDate; date.setDate(date.getDate() + 1)) {
                  const tmpSearchDate = new Date(date.valueOf());
                  let reserveCount = 0;
                  if (fireReservationFacility.reservation_count) {
                    reserveCount = fireReservationFacility.reservation_count;
                  }
                  if (fireSpecials) {
                    for (let reservationFacilitySpecial of fireSpecials) {
                      // 特別営業日で
                      if (reservationFacilitySpecial.reservation_special_date_type===2) {
                        if (reservationFacilitySpecial.reservation_special_start_date) {
                          // 単日
                          if (!reservationFacilitySpecial.reservation_special_end_date) {
                            if (tmpSearchDate.getTime() === new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime()) {
                              if (reservationFacilitySpecial.reservation_special_count) {
                                reserveCount = reservationFacilitySpecial.reservation_special_count;
                              }
                            }
                          // 期間
                          } else {
                            if (new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime() <= tmpSearchDate.getTime() &&
                                tmpSearchDate.getTime() <= new Date(reservationFacilitySpecial.reservation_special_end_date.seconds*1000).getTime()) {
                              if (reservationFacilitySpecial.reservation_special_count) {
                                reserveCount = reservationFacilitySpecial.reservation_special_count;
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  dailyCount.push([tmpSearchDate, 0, reserveCount]);
                }
                // 予約件数リストの[1]予約件数を更新
                if (reservations) {
                  for (let reservation of reservations) {
                    // キャンセルは予約件数に含めない
                    // 対象環境が違う予約は予約件数に含めない
                    if (reservation.reservation_status===AppConst.RESERVATION_STATUS_CANCEL
                      || reservationTarget === this.TARGET_PRODUCTION && reservation.preview 
                      || reservationTarget === this.TARGET_PREVIEW && !reservation.preview
                    ) {
                      continue;
                    }
                    // キャンセル以外の予約は日別に件数を集計する
                    if (reservation.reservation_start_date) {
                      if (!reservation.reservation_end_date) {
                        for (let dayCount of dailyCount) {
                          if (dayCount[0].getTime() === new Date(reservation.reservation_start_date.seconds*1000).getTime()) {
                            dayCount[1] += 1;
                          }
                        };
                      } else {
                        for (let date = new Date(reservation.reservation_start_date.seconds*1000); date <= new Date(reservation.reservation_end_date.seconds*1000); date.setDate(date.getDate() + 1)) {
                          for (let dayCount of dailyCount) {
                            if (dayCount[0].getTime() === date.getTime()) {
                              dayCount[1] += 1;
                            }
                          }
                        }
                      }
                    }
                  }
                }
                // 予約可能件数と実際の予約件数を比較して、予約可能件数を満たしている（満席）の場合は、NG日としてリストに追加する
                for (const dayCount of dailyCount) {
                  if (dayCount && dayCount.length == 3) {
                    if (dayCount[1] >= dayCount[2]) {
                      if (retArray.filter(ret => ret.date.getTime() === new Date(dayCount[0]).getTime()).length === 0) {
                        retArray.push({date: new Date(dayCount[0]), reason: 'full'});
                      }
                    }
                  }
                }
              // 時間枠タイプ（全ての時間帯で上限数を超えている日をNG日としてリストに追加）
              } else {
                for (let date = new Date(reserveStartDate.valueOf()); date <= reserveEndDate; date.setDate(date.getDate() + 1)) {
                  const tmpSearchDate = new Date(date.valueOf());
                  let timeCounts = [];
                  if (fireSpecials) {
                    for (let reservationFacilitySpecial of fireSpecials) {
                      // 特別営業日で
                      if (reservationFacilitySpecial.reservation_special_date_type===2) {
                        if (reservationFacilitySpecial.reservation_special_start_date) {
                          // 単日
                          if (!reservationFacilitySpecial.reservation_special_end_date) {
                            if (tmpSearchDate.getTime() === new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime()) {
                              if (reservationFacilitySpecial.reservation_select_times) {
                                for (const reservation_select_time of reservationFacilitySpecial.reservation_select_times) {
                                  timeCounts.push([reservation_select_time.index, reservation_select_time.value,0]);
                                }
                              }
                            }
                          // 期間
                          } else {
                            if (new Date(reservationFacilitySpecial.reservation_special_start_date.seconds*1000).getTime() <= tmpSearchDate.getTime() &&
                                tmpSearchDate.getTime() <= new Date(reservationFacilitySpecial.reservation_special_end_date.seconds*1000).getTime()) {
                              if (reservationFacilitySpecial.reservation_select_times) {
                                for (const reservation_select_time of reservationFacilitySpecial.reservation_select_times) {
                                  timeCounts.push([reservation_select_time.index, reservation_select_time.value,0]);
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  // 特別営業日に時間枠の設定がない（＝通常時間枠）
                  if (timeCounts.length < 1) {
                    if (fireReservationFacility.reservation_select_times) {
                      for (const reservation_select_time of fireReservationFacility.reservation_select_times) {
                        timeCounts.push([reservation_select_time.index, reservation_select_time.value,0]);
                      }
                    }
                  }
                  // 利用日の各時間帯の予約件数を取得する
                  for (let reservation of reservations) {
                    // キャンセルは予約件数に含めない
                    // 対象環境が違う予約は予約件数に含めない
                    if (reservation.reservation_status===AppConst.RESERVATION_STATUS_CANCEL
                      || reservationTarget === this.TARGET_PRODUCTION && reservation.preview 
                      || reservationTarget === this.TARGET_PREVIEW && !reservation.preview
                    ) {
                      continue;
                    }
                    // 同一の利用日でキャンセル以外の予約の場合に時間帯別の予約件数を集計する
                    if (reservation.reservation_start_date) {
                      if (tmpSearchDate.getTime() === new Date(reservation.reservation_start_date.seconds*1000).getTime()) {
                        if (reservation.reservation_times) {
                          for (let reservation_time of reservation.reservation_times) {
                            for (let timeCount of timeCounts) {
                              if (timeCount[0] === reservation_time['index']) {
                                timeCount[2] += 1;
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                  let isNg = true;
                  for (let timeCount of timeCounts) {
                    if (timeCount[1] > timeCount[2]) {
                      isNg = false;
                    }
                  }
                  if (isNg) {
                    if (retArray.filter(ret => ret.date.getTime() === tmpSearchDate.getTime()).length === 0) {
                      retArray.push({date: tmpSearchDate, reason: 'full'});
                    }
                  }
                }
              }
              }
            observer.next(retArray);
            observer.complete();
          });
        // 予約施設IDが取得できない場合は何もしない
        } else {
          observer.next();
          observer.complete();
        }
      });    
    });
  }
  
  // 予約施設の特別営業日を取得
  private getSpecialsByReservationFacilityId(reservationFacility: ReservationFacility, target: string): Observable<ReservationFacilitySpecial[]> {
    const projectId = reservationFacility.project_id;
    const prefix = target === this.TARGET_PRODUCTION ? '' : '_stg';
    return this.angularFirestore
      .collection<ReservationFacilitySpecial>(`reservation_facility_specials${prefix}`, ref => {
        return ref
          .where('project_id', '==', projectId)
          .where('reservation_facility_id', '==', reservationFacility.reservation_facility_id)
          .orderBy('reservation_special_start_date', 'asc')
      })
      .valueChanges()
      .pipe(take(1));
  }

  // 予約設備を取得(休業日情報)
  private getReservationFacility(reservationFacility: ReservationFacility, target: string): Observable<ReservationFacility | undefined> {
    const projectId = reservationFacility.project_id;
    const prefix = target === this.TARGET_PRODUCTION ? '' : '_stg';
    return this.angularFirestore
      .collection<ReservationFacility>(`reservation_facilities${prefix}`, ref => {
        return ref
          .where('project_id', '==', projectId)
          .where('reservation_facility_id', '==', reservationFacility.reservation_facility_id)
      })
      .valueChanges()
      .pipe(
        map((reservationFacilities) => {
          let reservationFacility: ReservationFacility | undefined;
          if (reservationFacilities.length > 0) {
            reservationFacility = reservationFacilities[0];
          }
          return reservationFacility;
        }),
        take(1)
      );
  }

  // 予約を取得
  private getReservation(reservationFacility: ReservationFacility, fromDate: Date, toDate: Date): Observable<Reservation[] | undefined> {
    const projectId = reservationFacility.project_id;
    return this.angularFirestore
    .collection<Reservation>(`reservations`, (ref) => ref.where('project_id', '==', projectId)
      .where('reservation_facility_id', '==', reservationFacility.reservation_facility_id)
      .where('reservation_start_date', '>=', fromDate)
      .where('reservation_start_date', '<=', toDate)
      .orderBy('reservation_start_date', 'desc')
    )
    .valueChanges({ idField: 'id' })
    .pipe(take(1));
  }
}
